import { Button } from "primereact/button";
import { Calendar, CalendarValueType } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useState, useRef } from "react";
import styled from "styled-components";

import { BulkClaimActionsBar } from "../../../components/BulkEditClaimsModal/BulkClaimActionsBar";
import { ClaimDetailsModal } from "../../../components/ClaimDetailsModal/ClaimDetailsModal";
import { parseDate } from "../../../helpers/date";
import useLocalStorageState from "../../../hooks/useLocalStorageState";
import {
  useGetClaimsQuery,
  useLazyExportClaimsQuery,
  useGetDistinctClassificationsQuery,
  useGetDistinctClearPayersQuery,
  useGetDistinctEdiFilesQuery,
  useGetClaimByIdQuery,
} from "../../../store/queries/claims";
import {
  IClaim,
  CLAIM_CATEGORIES,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  CHARGE_BELOW_CONTRACT_RATE_CATEGORIES,
  EFFECTIVE_YEAR_MISSING_CATEGORIES,
  TC_COMPONENT_VARIANT_CATEGORIES,
  RESOLUTION_STATUS_LABELS,
  RESOLUTION_STATUS_COLORS,
  RESOLUTION_FILTER_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  DENIAL_STATUS_FILTER_OPTIONS,
  DENIAL_CLASSIFICATION_FILTER_OPTIONS,
} from "../../../types/claim";
import {
  calculateOOPPercent,
  extractClaimPatientResponsibility,
  formatDollars,
} from "../../../utils/claimAdjustments";

const CaseSignatureLink = styled.span`
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0056b3;
  }
`;

const CompactDataTable = styled(DataTable)`
  /* Reduce cell padding */
  .p-datatable-tbody > tr > td {
    padding: 0.25rem 0.5rem !important;
    white-space: normal !important;
    word-wrap: break-word !important;
  }

  /* Reduce header padding and enable wrapping */
  .p-datatable-thead > tr > th {
    padding: 0.25rem 0.5rem !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    line-height: 1.3 !important;
  }

  /* Ensure column content wraps */
  .p-column-title {
    white-space: normal !important;
    word-wrap: break-word !important;
  }
`;

interface IClaimsListProps {
  accountId: string;
}

function ClaimsList({ accountId }: IClaimsListProps) {
  const toast = useRef<Toast>(null);

  // Filter state - non-persisted
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<Date[]>([]);

  // Unified filter - includes both payment types and categories (like CBCR)
  const [selectedFilters, setSelectedFilters] = useLocalStorageState<string[]>(
    `clearhealth_claims_filters_selected_${accountId}`,
    ["all"]
  );

  // CLE-2225: denial status filter — orthogonal to payment type, combinable.
  const [selectedDenialStatuses, setSelectedDenialStatuses] =
    useLocalStorageState<string[]>(
      `clearhealth_claims_filters_denialStatuses_${accountId}`,
      []
    );

  // CLE-2320: denial classification filter — orthogonal to denial status. Narrows to
  // claims whose Claim.denialClassificationCode matches one of the selected codes.
  const [selectedDenialClassifications, setSelectedDenialClassifications] =
    useLocalStorageState<string[]>(
      `clearhealth_claims_filters_denialClassifications_${accountId}`,
      []
    );

  // Filter state - persisted to localStorage
  const [minDifferencePercent, setMinDifferencePercent] = useLocalStorageState<
    number | undefined
  >(`clearhealth_claims_filters_minPercent_${accountId}`, undefined);

  // CLE-2166: numeric range filter on episode event count.
  const [minEventCount, setMinEventCount] = useLocalStorageState<
    number | undefined
  >(`clearhealth_claims_filters_minEvents_${accountId}`, undefined);
  const [maxEventCount, setMaxEventCount] = useLocalStorageState<
    number | undefined
  >(`clearhealth_claims_filters_maxEvents_${accountId}`, undefined);

  // Resolution status filter - default to "not_reviewed" to show unprocessed items
  const [resolutionFilter, setResolutionFilter] = useLocalStorageState<
    "not_reviewed" | "in_review" | "all"
  >(`clearhealth_claims_filters_resolution_${accountId}`, "not_reviewed");

  // AI Classification filter - default to "all" to show everything
  const [classificationFilter, setClassificationFilter] =
    useLocalStorageState<string>(
      `clearhealth_claims_filters_classification_${accountId}`,
      "all"
    );

  // Clear Payer filter - default to null (show all); stores payer id as string or 'no-match'
  const [payerIdFilter, setPayerIdFilter] = useState<string | null>(null);

  // CLE-2491: EDI file filter - persisted to localStorage; empty array = show all files
  const [ediFileFilter, setEdiFileFilter] = useLocalStorageState<string[]>(
    `clearhealth_claims_filters_ediFiles_${accountId}`,
    []
  );

  // Patient class filter - persisted to localStorage
  const [patientClassFilter, setPatientClassFilter] = useLocalStorageState<
    "inpatient" | "outpatient" | "all"
  >(`clearhealth_claims_filters_patientClass_${accountId}`, "all");

  // Claim category filter (form type + place of service) - persisted to localStorage
  const [claimCategoryFilter, setClaimCategoryFilter] = useLocalStorageState<
    | "institutional"
    | "professional-fac"
    | "professional-nonfac"
    | "anesthesia"
    | "all"
  >(`clearhealth_claims_filters_claimCategory_${accountId}`, "all");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Sorting state - default to difference ascending (most negative first = largest underpayments)
  const [sortField, setSortField] = useState("serviceDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IClaim | null>(null);

  // CLE-2172: bulk-edit selection (single-page only). Clears on every page,
  // pageSize, sort, or filter change. Backend caps at 25 per call; default
  // pageSize is 25 so "select all on page" naturally hits the cap. The
  // `isDataSelectable` guard prevents going over on the 50-per-page view.
  const BULK_CLAIM_UPDATE_MAX = 25;
  const [selectedClaims, setSelectedClaims] = useState<IClaim[]>([]);

  // Navigation state for similar claims - maintains history stack for back navigation
  const [navigatedClaimId, setNavigatedClaimId] = useState<string | null>(null);
  const [claimHistory, setClaimHistory] = useState<IClaim[]>([]);

  // Compute API params from unified dropdown selection
  const hasCBCR = selectedFilters.includes(
    "CATEGORY_CHARGE_BELOW_CONTRACT_RATE"
  );
  // CLE-2326: the new data-quality bucket — claims priced off a contract that doesn't cover
  // the claim's date of service. Behaves like CBCR: opt in to view; excluded from default
  // payment-type views.
  const hasEYM = selectedFilters.includes("CATEGORY_EFFECTIVE_YEAR_MISSING");
  // CLE-2462: opt-in bucket for TC-component-variant claims (hidden from the default
  // underpayment worklist). Selecting it forwards the category so the backend opts them back in.
  const hasTC = selectedFilters.includes("CATEGORY_TC_COMPONENT_VARIANT");
  const hasAll = selectedFilters.includes("all");
  const paymentTypeValues = selectedFilters.filter(
    (v) => !v.startsWith("CATEGORY_")
  );

  const apiPaymentTypes =
    hasAll || paymentTypeValues.length === 0 ? ["all"] : paymentTypeValues;

  const getApiCBCRFilter = (): "include" | "exclude" | "only" => {
    if (hasAll) return "include";
    if (hasCBCR && paymentTypeValues.length === 0) return "only";
    if (hasCBCR) return "include";
    return "exclude";
  };
  const apiCBCRFilter = getApiCBCRFilter();

  const getApiEYMFilter = (): "include" | "exclude" | "only" => {
    if (hasAll) return "include";
    if (hasEYM && paymentTypeValues.length === 0 && !hasCBCR) return "only";
    if (hasEYM) return "include";
    return "exclude";
  };
  const apiEYMFilter = getApiEYMFilter();

  // Export state
  const [exportTrigger, { isLoading: isExporting }] =
    useLazyExportClaimsQuery();

  // Fetch distinct classifications for the filter dropdown
  const { data: classificationsData } = useGetDistinctClassificationsQuery({
    accountId,
  });

  // Fetch distinct Clear payers for the filter dropdown
  const { data: clearPayersData } = useGetDistinctClearPayersQuery({
    accountId,
  });

  // CLE-2491: fetch the EDI files that produced claims for this account (filter dropdown)
  const { data: ediFilesData } = useGetDistinctEdiFilesQuery({ accountId });

  // Fetch navigated claim by ID (for similar claims navigation)
  const { data: navigatedClaimData, isFetching: isNavigatedClaimFetching } =
    useGetClaimByIdQuery(
      { claimId: navigatedClaimId! },
      { skip: !navigatedClaimId }
    );

  // Handle navigation to a similar claim - push current claim to history
  const handleNavigateToClaim = (claimId: string) => {
    if (selectedRow) {
      setClaimHistory((prev) => [...prev, selectedRow]);
    }
    setNavigatedClaimId(claimId);
  };

  // Handle back navigation - pop from history
  const handleBackToClaim = () => {
    if (claimHistory.length > 0) {
      const previousClaim = claimHistory[claimHistory.length - 1];
      setClaimHistory((prev) => prev.slice(0, -1));
      setSelectedRow(previousClaim);
    }
  };

  // Update selectedRow when navigated claim data is fetched
  React.useEffect(() => {
    if (navigatedClaimData && !isNavigatedClaimFetching) {
      setSelectedRow(navigatedClaimData);
      setNavigatedClaimId(null); // Clear navigation state
    }
  }, [navigatedClaimData, isNavigatedClaimFetching]);

  // Clear history when modal is closed
  const handleCloseModal = () => {
    setSelectedRow(null);
    setClaimHistory([]);
  };

  // Fetch data - filter by payment types
  const { data, isLoading, isError, error } = useGetClaimsQuery({
    accountId,
    search: search || undefined,
    startDate: dateRange[0] || undefined,
    endDate: dateRange[1] || undefined,
    sortField,
    sortOrder,
    page,
    pageSize,
    minDifferencePercent,
    minEventCount,
    maxEventCount,
    chargeBelowContractRateFilter: apiCBCRFilter,
    effectiveYearMissingFilter: apiEYMFilter,
    // CLE-2462: forwarding the TC category opts the worklist-suppressed TC claims back in.
    categories: hasTC ? ["TC_COMPONENT_VARIANT"] : undefined,
    paymentTypes: apiPaymentTypes,
    denialStatuses:
      selectedDenialStatuses.length > 0 ? selectedDenialStatuses : undefined,
    denialClassificationCodes:
      selectedDenialClassifications.length > 0
        ? selectedDenialClassifications
        : undefined,
    resolutionFilter,
    classificationFilter:
      classificationFilter === "all" ? undefined : classificationFilter,
    payerIdFilter: payerIdFilter || undefined,
    patientClassFilter:
      patientClassFilter === "all" ? undefined : patientClassFilter,
    claimCategoryFilter:
      claimCategoryFilter === "all" ? undefined : claimCategoryFilter,
    ediConversionFileIds: ediFileFilter.length > 0 ? ediFileFilter : undefined,
  });

  // Show error toast
  React.useEffect(() => {
    if (isError) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: (error as any)?.data?.message || "Failed to load claims",
      });
    }
  }, [isError, error]);

  // Currency formatter
  const formatCurrency = (cents: number | null): string => {
    if (cents === null || cents === undefined) return "-";
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };

  // Template functions
  const dateTemplate = (
    rowData: IClaim,
    field: "patientDOB" | "serviceDate"
  ) => {
    const date = rowData[field];
    return date ? parseDate(date) : "-";
  };

  const currencyTemplate = (
    rowData: IClaim,
    field:
      | "chargeAmount"
      | "expectedAmount"
      | "claimAmount"
      | "amountDifference"
  ) => {
    return formatCurrency(rowData[field]);
  };

  // Total Charges template with badge for charge below contract rate
  // (variance-flavored, "info" severity) or CLE-2326 EFFECTIVE_YEAR_MISSING
  // (data-quality warning — under/overpayment label is unreliable for these rows).
  // Charge-Based Pricing claims intentionally do not show a badge (PM decision).
  const chargeAmountTemplate = (rowData: IClaim) => {
    const formattedAmount = formatCurrency(rowData.chargeAmount);
    const category = rowData.category ?? "";
    const isChargeBelowContract =
      CHARGE_BELOW_CONTRACT_RATE_CATEGORIES.includes(category);
    const isEffectiveYearMissing =
      EFFECTIVE_YEAR_MISSING_CATEGORIES.includes(category);
    const isTcComponentVariant =
      TC_COMPONENT_VARIANT_CATEGORIES.includes(category);

    if (
      !isChargeBelowContract &&
      !isEffectiveYearMissing &&
      !isTcComponentVariant
    ) {
      return formattedAmount;
    }

    const categoryKey =
      rowData.category ?? CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE;

    const icon = isEffectiveYearMissing
      ? "pi pi-exclamation-triangle"
      : "pi pi-info-circle";

    return (
      <div className="flex flex-column align-items-start gap-1">
        <span>{formattedAmount}</span>
        <Tag
          value={CATEGORY_LABELS[categoryKey]}
          severity={CATEGORY_COLORS[categoryKey]}
          icon={icon}
        />
      </div>
    );
  };

  const nullableTemplate = (value: string | null) => {
    return value || "-";
  };

  // Variance % template - calculate percentage difference
  const variancePercentTemplate = (rowData: IClaim) => {
    if (
      rowData.expectedAmount === null ||
      rowData.amountDifference === null ||
      rowData.expectedAmount === 0
    ) {
      return "-";
    }

    // Formula: (Math.abs(amountDifference) / expectedAmount) * 100
    const percentage =
      (Math.abs(rowData.amountDifference) / rowData.expectedAmount) * 100;

    return `${percentage.toFixed(1)}%`;
  };

  // Helper function to format case signature with line breaks (4 codes per line)
  const formatCaseSignatureWithLineBreaks = (caseSignature: string) => {
    // Split by comma to get array of "CODE:qty" items
    const items = caseSignature.split(",");

    // Group into chunks of 4
    const chunks: string[][] = [];
    for (let i = 0; i < items.length; i += 4) {
      chunks.push(items.slice(i, i + 4));
    }

    // Return JSX with each chunk on its own line
    return (
      <>
        {chunks.map((chunk, index) => (
          <div key={index}>{chunk.join(",")}</div>
        ))}
      </>
    );
  };

  // Patient name link template - opens modal
  const patientNameTemplate = (rowData: IClaim) => {
    const handleClick = () => {
      setSelectedRow(rowData);
      setModalVisible(true);
    };

    return (
      <CaseSignatureLink onClick={handleClick}>
        <span data-pii="patient-name">{rowData.patientName}</span>
      </CaseSignatureLink>
    );
  };

  // Case signature template - plain text with line breaks
  const caseSignatureTemplate = (rowData: IClaim) => {
    return formatCaseSignatureWithLineBreaks(rowData.caseSignature);
  };

  // Accuracy status template - shows icon based on allowable accuracy
  const accuracyStatusTemplate = (rowData: IClaim) => {
    if (!rowData.precalculatedAllowable) {
      return <span>-</span>;
    }

    const { allowableAccuracyStatus, missingCodes } =
      rowData.precalculatedAllowable;

    if (allowableAccuracyStatus) {
      return (
        <span
          title="Complete - All procedure codes found"
          style={{ color: "#28a745" }}
        >
          <i className="pi pi-check-circle" style={{ fontSize: "1.2rem" }} />
        </span>
      );
    }

    const missingCount = missingCodes?.length || 0;
    return (
      <span
        title={`Incomplete - ${missingCount} code(s) missing`}
        style={{ color: "#ffc107" }}
      >
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: "1.2rem" }}
        />
      </span>
    );
  };

  // Matched 837 indicator - green check if an 837 claim was matched to this 835
  const matched837Template = (rowData: IClaim) => {
    const count = rowData.matched837Claims?.length ?? 0;
    if (count === 0) {
      return <span>-</span>;
    }
    return (
      <span
        title={`${count} matched 837 claim${count === 1 ? "" : "s"}`}
        style={{ color: "#28a745" }}
      >
        <i className="pi pi-check-circle" style={{ fontSize: "1.2rem" }} />
      </span>
    );
  };

  // ===== CLAIM-SIDE TEMPLATES (from 835 PR adjustments) =====
  // Note: 835 adjustment amounts are in dollars (not cents) - use formatDollars

  // Claim patient responsibility (deductible + copay + coinsurance)
  const claimPatientOOPTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.totalOOP > 0 ? formatDollars(resp.totalOOP) : "-";
  };

  // Claim deductible (PR adjustment code 1)
  const claimDeductibleTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.deductible > 0 ? formatDollars(resp.deductible) : "-";
  };

  // Claim coinsurance (PR adjustment code 2)
  const claimCoinsuranceTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.coinsurance > 0 ? formatDollars(resp.coinsurance) : "-";
  };

  // Claim copay (PR adjustment code 3)
  const claimCopayTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.copay > 0 ? formatDollars(resp.copay) : "-";
  };

  // Coinsurance percent = coinsurance / (claim amount – deductible)
  // Uses claim-side values (from 835 PR adjustments) in dollars
  const coinsurancePercentTemplate = (rowData: IClaim) => {
    // Get claim-side values (in dollars from 835)
    const claimResp = extractClaimPatientResponsibility(rowData.lineItems);

    // Get claim amount (in cents) - this is the allowable/paid amount from 835
    if (!rowData.claimAmount) return "-";

    const claimAmountInDollars = rowData.claimAmount / 100;

    // Calculate: Coinsurance% = coinsurance / (claimAmount - deductible)
    const coinsurancePercent = calculateOOPPercent(
      claimResp.coinsurance, // Just coinsurance portion
      claimAmountInDollars,
      claimResp.deductible
    );

    if (coinsurancePercent === null) return "-";
    return `${coinsurancePercent.toFixed(1)}%`;
  };

  // Match confidence indicator
  const matchConfidenceTemplate = (rowData: IClaim) => {
    if (!rowData.order || rowData.orderMatchConfidence === null) {
      return <span>-</span>;
    }

    const confidence = rowData.orderMatchConfidence ?? 0;
    let color: string;
    let title: string;

    if (confidence >= 0.9) {
      color = "#28a745"; // green
      title = `High confidence (${(confidence * 100).toFixed(0)}%)`;
    } else if (confidence >= 0.7) {
      color = "#ffc107"; // yellow
      title = `Medium confidence (${(confidence * 100).toFixed(0)}%)`;
    } else {
      color = "#dc3545"; // red
      title = `Low confidence (${(confidence * 100).toFixed(0)}%)`;
    }

    return (
      <span title={title} style={{ color }}>
        <i className="pi pi-link" style={{ fontSize: "1.2rem" }} />
      </span>
    );
  };

  // Resolution status template - shows color-coded status badge
  const resolutionStatusTemplate = (rowData: IClaim) => {
    const status = rowData.resolutionStatus;
    const label = RESOLUTION_STATUS_LABELS[status || "null"];
    const severity = RESOLUTION_STATUS_COLORS[status || "null"];

    return <Tag value={label} severity={severity} />;
  };

  // Handle DataTable events
  const onSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder === 1 ? "asc" : "desc");
    setPage(1); // Reset to first page
    setSelectedClaims([]);
  };

  const onPage = (event: any) => {
    setPage(event.page + 1); // PrimeReact uses 0-indexed pages
    setPageSize(event.rows);
    setSelectedClaims([]);
  };

  // Handle filter changes - reset to page 1
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleDateRangeChange = (e: { value: CalendarValueType }) => {
    const dates = e.value as Date[];
    setDateRange(dates || []);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleMinDifferencePercentChange = (value: string) => {
    const numValue = value === "" ? undefined : parseFloat(value);
    setMinDifferencePercent(numValue);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleEventCountChange =
    (setter: (value: number | undefined) => void) => (value: string) => {
      const numValue = value === "" ? undefined : parseInt(value, 10);
      setter(Number.isNaN(numValue as number) ? undefined : numValue);
      setPage(1);
      setSelectedClaims([]);
    };

  const handleResolutionFilterChange = (
    value: "not_reviewed" | "in_review" | "all"
  ) => {
    setResolutionFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleClassificationFilterChange = (value: string) => {
    setClassificationFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleFiltersChange = (value: string[]) => {
    const prevHadAll = selectedFilters.includes("all");
    const nowHasAll = value.includes("all");
    const hasCBCRValue = value.includes("CATEGORY_CHARGE_BELOW_CONTRACT_RATE");

    if (nowHasAll && !prevHadAll) {
      setSelectedFilters(["all"]);
    } else if (nowHasAll && value.length > 1) {
      setSelectedFilters(value.filter((v) => v !== "all"));
    } else {
      setSelectedFilters(value);
    }
    setPage(1);
    setSelectedClaims([]);
  };

  const handlePayerIdFilterChange = (value: string | null) => {
    setPayerIdFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  // CLE-2491: EDI file filter change
  const handleEdiFileFilterChange = (value: string[]) => {
    setEdiFileFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  const handlePatientClassFilterChange = (
    value: "inpatient" | "outpatient" | "all"
  ) => {
    setPatientClassFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleClaimCategoryFilterChange = (
    value:
      | "institutional"
      | "professional-fac"
      | "professional-nonfac"
      | "anesthesia"
      | "all"
  ) => {
    setClaimCategoryFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  // Handle export button click
  const handleExport = async () => {
    try {
      const result = await exportTrigger({
        accountId,
        search: search || undefined,
        startDate: dateRange[0] || undefined,
        endDate: dateRange[1] || undefined,
        sortField,
        sortOrder,
        minDifferencePercent,
        chargeBelowContractRateFilter: apiCBCRFilter,
        effectiveYearMissingFilter: apiEYMFilter,
        // CLE-2462: keep export parity with the visible list — opt TC claims back in.
        categories: hasTC ? ["TC_COMPONENT_VARIANT"] : undefined,
        paymentTypes: apiPaymentTypes,
        denialStatuses:
          selectedDenialStatuses.length > 0
            ? selectedDenialStatuses
            : undefined,
        denialClassificationCodes:
          selectedDenialClassifications.length > 0
            ? selectedDenialClassifications
            : undefined,
        resolutionFilter,
        classificationFilter:
          classificationFilter === "all" ? undefined : classificationFilter,
        payerIdFilter: payerIdFilter || undefined,
        patientClassFilter:
          patientClassFilter === "all" ? undefined : patientClassFilter,
        claimCategoryFilter:
          claimCategoryFilter === "all" ? undefined : claimCategoryFilter,
        ediConversionFileIds:
          ediFileFilter.length > 0 ? ediFileFilter : undefined,
      }).unwrap();

      // Download the file using the signed URL
      window.open(result.downloadUrl, "_blank");

      toast.current?.show({
        severity: "success",
        summary: "Export Started",
        detail: "Your Excel file is being downloaded",
      });
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Export Failed",
        detail: (err as any)?.data?.message || "Failed to export claims",
      });
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
      </div>
    );
  }

  const claims = data?.data || [];
  const totalRecords = data?.totalCount || 0;

  return (
    <>
      <Toast ref={toast} />

      {/* Filters Row 1: Search, Date Range, Category */}
      <div className="flex gap-3 mb-2 flex-wrap align-items-center">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search patient, payer, account #, claim #, or CPT codes..."
            className="w-20rem"
          />
        </span>

        <div className="flex flex-column gap-2" style={{ minWidth: "250px" }}>
          <Calendar
            selectionMode="range"
            placeholder="Service Date Range"
            value={dateRange}
            onChange={handleDateRangeChange}
            showIcon
            dateFormat="mm/dd/yy"
          />
        </div>

        {/* Category Filter (Payment Types + Categories like CBCR) */}
        <div className="flex align-items-center gap-2">
          <label htmlFor="paymentTypesFilter">Category:</label>
          <MultiSelect
            inputId="paymentTypesFilter"
            value={selectedFilters}
            options={PAYMENT_TYPE_OPTIONS}
            onChange={(e) => handleFiltersChange(e.value)}
            placeholder="Select payment types"
            display="chip"
            style={{ minWidth: "220px" }}
            showSelectAll={false}
          />
        </div>

        {/* CLE-2225: Denial Status filter — orthogonal to payment type, combinable. */}
        <div className="flex align-items-center gap-2">
          <label htmlFor="denialStatusFilter">Denial Status:</label>
          <MultiSelect
            inputId="denialStatusFilter"
            value={selectedDenialStatuses}
            options={DENIAL_STATUS_FILTER_OPTIONS}
            onChange={(e) => {
              setSelectedDenialStatuses(e.value);
              setPage(1);
              setSelectedClaims([]);
            }}
            placeholder="Any denial status"
            display="chip"
            style={{ minWidth: "200px" }}
            showSelectAll={false}
          />
        </div>

        {/* CLE-2320: Denial Classification filter — narrows by deterministic
            classification code (e.g., D3 = Non-Covered, E1 = Prior Auth Failure). */}
        <div className="flex align-items-center gap-2">
          <label htmlFor="denialClassificationFilter">
            Denial Classification:
          </label>
          <MultiSelect
            inputId="denialClassificationFilter"
            value={selectedDenialClassifications}
            options={DENIAL_CLASSIFICATION_FILTER_OPTIONS}
            onChange={(e) => {
              setSelectedDenialClassifications(e.value);
              setPage(1);
              setSelectedClaims([]);
            }}
            placeholder="Any classification"
            display="chip"
            filter
            style={{ minWidth: "240px", maxWidth: "340px" }}
            showSelectAll={false}
          />
        </div>
      </div>

      {/* Filters Row 2: Status, Classification, Other Filters, Export */}
      <div className="flex gap-3 mb-3 flex-wrap align-items-center justify-content-between">
        <div className="flex gap-3 flex-wrap align-items-center">
          {/* Resolution Status Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="resolutionFilter">Status:</label>
            <Dropdown
              inputId="resolutionFilter"
              value={resolutionFilter}
              options={[...RESOLUTION_FILTER_OPTIONS]}
              onChange={(e) => handleResolutionFilterChange(e.value)}
              placeholder="Select status"
            />
          </div>

          {/* AI Classification Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="classificationFilter">Classification:</label>
            <Dropdown
              inputId="classificationFilter"
              value={classificationFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Not Analyzed", value: "not_analyzed" },
                ...(classificationsData || []).map((c) => ({
                  label: `${c.code} - ${c.title}`,
                  value: c.code,
                })),
              ]}
              onChange={(e) => handleClassificationFilterChange(e.value)}
              placeholder="Select classification"
              style={{ minWidth: "180px" }}
            />
          </div>

          {/* Clear Payer Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="payerIdFilter">Clear Payer:</label>
            <Dropdown
              inputId="payerIdFilter"
              value={payerIdFilter}
              options={[
                { label: "All", value: null },
                ...(clearPayersData || []).map((payer) => ({
                  label: payer.name,
                  value: payer.id === null ? "no-match" : String(payer.id),
                })),
              ]}
              onChange={(e) => handlePayerIdFilterChange(e.value)}
              placeholder="Select payer"
              showClear
              style={{ minWidth: "180px" }}
            />
          </div>

          {/* EDI File Filter (CLE-2491) */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="ediFileFilter">EDI File:</label>
            <MultiSelect
              inputId="ediFileFilter"
              value={ediFileFilter}
              options={(ediFilesData || []).map((file) => ({
                label: `${file.displayName || file.fileName} (${
                  file.claimCount
                })`,
                value: file.id,
              }))}
              onChange={(e) => handleEdiFileFilterChange(e.value)}
              placeholder="All files"
              display="chip"
              filter
              showClear
              maxSelectedLabels={2}
              style={{ minWidth: "220px", maxWidth: "360px" }}
            />
          </div>

          {/* Patient Class Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="patientClassFilter">Patient Class:</label>
            <Dropdown
              inputId="patientClassFilter"
              value={patientClassFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Inpatient", value: "inpatient" },
                { label: "Outpatient", value: "outpatient" },
              ]}
              onChange={(e) => handlePatientClassFilterChange(e.value)}
              placeholder="Select class"
            />
          </div>

          {/* Claim Category Filter (form type + place of service) */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="claimCategoryFilter">Claim Type:</label>
            <Dropdown
              inputId="claimCategoryFilter"
              value={claimCategoryFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Institutional", value: "institutional" },
                {
                  label: "Professional — Facility",
                  value: "professional-fac",
                },
                {
                  label: "Professional — Non-Facility",
                  value: "professional-nonfac",
                },
                { label: "Anesthesia", value: "anesthesia" },
              ]}
              onChange={(e) => handleClaimCategoryFilterChange(e.value)}
              placeholder="Select claim type"
              style={{ minWidth: "200px" }}
            />
          </div>

          <div className="flex align-items-center gap-2">
            <label htmlFor="minDifferencePercent">Min Difference %:</label>
            <InputText
              id="minDifferencePercent"
              type="number"
              value={minDifferencePercent?.toString() ?? ""}
              onChange={(e) => handleMinDifferencePercentChange(e.target.value)}
              className="w-8rem"
              step="1"
            />
          </div>

          {/* CLE-2166: Events numeric range filter. >=2 surfaces complex episodes
              (reprocess chains, bilateral splits, duplicate denials) for review. */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="minEventCount">Events:</label>
            <InputText
              id="minEventCount"
              type="number"
              placeholder="Min"
              value={minEventCount?.toString() ?? ""}
              onChange={(e) =>
                handleEventCountChange(setMinEventCount)(e.target.value)
              }
              className="w-5rem"
              min="0"
              step="1"
            />
            <span>-</span>
            <InputText
              id="maxEventCount"
              type="number"
              placeholder="Max"
              value={maxEventCount?.toString() ?? ""}
              onChange={(e) =>
                handleEventCountChange(setMaxEventCount)(e.target.value)
              }
              className="w-5rem"
              min="0"
              step="1"
            />
          </div>
        </div>

        {/* Export Button */}
        <Button
          label="Export to Excel"
          icon="pi pi-file-excel"
          className="p-button-success"
          onClick={handleExport}
          loading={isExporting}
          disabled={isLoading || totalRecords === 0}
        />
      </div>

      {/* CLE-2172: bulk actions bar — only renders when the user has selected
          rows. Workflow is single-page: pick rows on this page, apply, then
          paginate and repeat. */}
      {selectedClaims.length > 0 && (
        <BulkClaimActionsBar
          selectedClaims={selectedClaims}
          maxBulkSize={BULK_CLAIM_UPDATE_MAX}
          onClearSelection={() => setSelectedClaims([])}
          accountId={accountId}
          onActionSuccess={() => setSelectedClaims([])}
        />
      )}

      {/* Data Table */}
      <CompactDataTable
        value={claims}
        lazy
        paginator
        first={(page - 1) * pageSize}
        rows={pageSize}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder === "asc" ? 1 : -1}
        rowsPerPageOptions={[10, 25, 50]}
        stripedRows
        responsiveLayout="scroll"
        emptyMessage={data?.message || "No claims found."}
        selectionMode="checkbox"
        selection={selectedClaims}
        onSelectionChange={(e) => setSelectedClaims(e.value as IClaim[])}
        dataKey="id"
        // Past the cap, disable further row checks. Users on the 50/page view
        // can still de-select; already-selected rows remain selectable.
        isDataSelectable={(e: { data: IClaim }) =>
          selectedClaims.length < BULK_CLAIM_UPDATE_MAX ||
          selectedClaims.some((c) => c.id === e.data.id)
        }
      >
        <Column selectionMode="multiple" style={{ width: "3rem" }} />
        <Column
          field="patientName"
          header="Patient Name"
          sortable
          body={patientNameTemplate}
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="serviceDate"
          header="Service Date"
          sortable
          body={(rowData) => dateTemplate(rowData, "serviceDate")}
          style={{ minWidth: "8rem" }}
        />
        {/* CLE-2166: child-event count for the episode. Single-event episodes show 1;
            anything >1 indicates a reprocess chain, bilateral split, or duplicate denial. */}
        <Column
          field="eventCount"
          header="Events"
          sortable
          body={(rowData) =>
            rowData.eventCount != null ? rowData.eventCount : "—"
          }
          style={{ minWidth: "5rem" }}
        />
        <Column
          field="claimPayerName"
          header="Payer Name"
          sortable
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="claimNetworkStatus"
          header="Network Status"
          body={(rowData) => nullableTemplate(rowData.claimNetworkStatus)}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="clearPayerName"
          header="Matched Payer"
          body={(rowData) => nullableTemplate(rowData.clearPayerName)}
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="chargeAmount"
          header="Total Charges"
          sortable
          body={chargeAmountTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="claimAmount"
          header="Claim Amount"
          sortable
          body={(rowData) => currencyTemplate(rowData, "claimAmount")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="expectedAmount"
          header="Expected Amount"
          sortable
          body={(rowData) => currencyTemplate(rowData, "expectedAmount")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="amountDifference"
          header="Difference"
          sortable
          body={(rowData) => currencyTemplate(rowData, "amountDifference")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          header="Variance"
          body={variancePercentTemplate}
          sortable
          sortField="variancePercent"
          style={{ minWidth: "7rem" }}
        />
        <Column
          field="caseSignature"
          header="Case Signature"
          body={caseSignatureTemplate}
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="allowableAccuracyStatus"
          header="Accuracy"
          sortable
          body={accuracyStatusTemplate}
          style={{ minWidth: "6rem", textAlign: "center" }}
        />
        <Column
          header="Matched 837"
          body={matched837Template}
          style={{ minWidth: "7rem", textAlign: "center" }}
        />
        {/* Claim-side columns from 835 PR adjustments */}
        <Column
          header="Claim Copay"
          body={claimCopayTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Claim Deductible"
          body={claimDeductibleTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Claim Coinsurance"
          body={claimCoinsuranceTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Coinsurance %"
          body={coinsurancePercentTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Claim Pt OOP"
          body={claimPatientOOPTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Match"
          body={matchConfidenceTemplate}
          style={{ minWidth: "5rem", textAlign: "center" }}
        />
        <Column
          field="resolutionStatus"
          header="Status"
          body={resolutionStatusTemplate}
          style={{ minWidth: "8rem" }}
        />
      </CompactDataTable>

      {/* Claim Details Modal */}
      {selectedRow && (
        <ClaimDetailsModal
          visible={modalVisible}
          onHide={() => {
            setModalVisible(false);
            handleCloseModal();
          }}
          accountId={accountId}
          claim={selectedRow}
          onNavigateToClaim={handleNavigateToClaim}
          onBackToClaim={
            claimHistory.length > 0 ? handleBackToClaim : undefined
          }
          isNavigating={isNavigatedClaimFetching}
          onClaimUpdated={(updatedClaim) => {
            // Update the selected claim in the modal
            setSelectedRow(updatedClaim);
            // Note: The claims list is automatically refreshed via RTK Query's
            // cache invalidation (invalidatesTags: ["Claims"] in the toggle mutation)
          }}
        />
      )}
    </>
  );
}

export default ClaimsList;
